import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { resetPassword } from "../lib/api";
import { useT } from "../lib/i18n";

export default function ResetPassword() {
  const { t, lang } = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error(t("reset.expired"));
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      toast.success(t("toast.reset_ok"));
      setTimeout(() => navigate("/login", { replace: true }), 800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("reset.expired"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFBFD]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[#E8E8ED]">
        <div className="max-w-md mx-auto px-4 md:px-6 h-14 flex items-center gap-3">
          <Link to="/login" className="p-2 -ml-2 rounded-full hover:bg-[#F5F5F7]" data-testid="reset-back-btn">
            <ArrowLeft className="w-5 h-5 text-[#0A2540]" />
          </Link>
          <div className="font-semibold text-[#0A2540]">{t("reset.title")}</div>
        </div>
      </header>
      <main className="max-w-md mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col items-center mb-6">
          <img src="/taip-logo.png" alt="TAi-P" className="w-20 h-20 object-contain" />
          <div className="text-sm text-gray-500 mt-2 text-center">{t("reset.desc")}</div>
        </div>
        {!token ? (
          <p data-testid="reset-no-token" className="text-sm text-red-500 text-center">
            {t("reset.expired")}
          </p>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">{t("login.password")}</label>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="reset-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  placeholder={t("login.password_ph")}
                  className="w-full pl-10 pr-4 py-3 rounded-full bg-white border border-[#E8E8ED] focus:border-[#4FB3E8] outline-none text-sm"
                />
              </div>
            </div>
            <button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full py-3 rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white font-medium transition-colors disabled:opacity-60"
            >
              {busy ? (lang === "en" ? "Please wait…" : "Sila tunggu…") : t("reset.submit")}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
